import { useState } from 'react';
import { Alert, Button, Checkbox, Space, Table, Typography, message } from 'antd';
import { TeamOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  recommendFleetOperatorsForTrip,
  recommendFleetOperatorsForJob,
  formatDistance,
  formatFleetOperatorRecommendation,
} from '@ubus/shared';
import type { FleetOperatorRecommendation } from '@ubus/shared';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface FleetOperatorRecommendationListProps {
  tripId?: string;
  jobId?: string;
  /** Required passenger count for this job/trip (shown in recommend table). */
  passengerCount?: number | null;
  onSelect: (rec: FleetOperatorRecommendation) => void;
  compact?: boolean;
}

function FleetOperatorRecommendationList({
  tripId,
  jobId,
  passengerCount,
  onSelect,
  compact = false,
}: FleetOperatorRecommendationListProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<FleetOperatorRecommendation[]>([]);
  const [loaded, setLoaded] = useState(false);
  /** Default off: recommend may include vehicles/drivers not currently accepting tasks. */
  const [requireAccepting, setRequireAccepting] = useState(false);
  const targetId = jobId || tripId;

  const load = async (acceptingOnly = requireAccepting) => {
    if (!targetId) return;
    setLoading(true);
    try {
      const limit = compact ? 3 : 5;
      const data = jobId
        ? await recommendFleetOperatorsForJob(jobId, limit, acceptingOnly)
        : await recommendFleetOperatorsForTrip(tripId!, limit, acceptingOnly);
      setRows(data);
      setLoaded(true);
      if (data.length === 0) {
        message.info(t('fleetOpsRec.empty'));
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('fleetOpsRec.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!targetId) {
    return (
      <Alert
        type="info"
        showIcon
        icon={<TeamOutlined />}
        message={t('fleetOpsRec.selectTarget', {
          defaultValue: 'Select a trip or job to recommend nearby fleet operators.',
        })}
        style={{ marginBottom: 16 }}
      />
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <Space style={{ marginBottom: 8 }} wrap>
        <Checkbox
          checked={requireAccepting}
          onChange={(e) => {
            const next = e.target.checked;
            setRequireAccepting(next);
            if (loaded) void load(next);
          }}
        >
          {t('fleetOpsRec.requireAccepting')}
        </Checkbox>
        <Button
          type="default"
          icon={<TeamOutlined />}
          loading={loading}
          onClick={() => void load()}
        >
          {loaded ? t('fleetOpsRec.refresh') : t('fleetOpsRec.find')}
        </Button>
        {loaded && (
          <Button type="text" icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
            {t('fleetOpsRec.reload')}
          </Button>
        )}
      </Space>

      {loaded && rows.length > 0 && (
        <Table
          size="small"
          rowKey={(r) => `${r.fleet_operator_id}-${r.vehicle_id}`}
          pagination={false}
          dataSource={rows}
          columns={[
            {
              title: t('fleetOpsRec.columns.owner'),
              dataIndex: 'operator_name',
              key: 'operator_name',
            },
            {
              title: t('fleetOpsRec.columns.vehicle'),
              key: 'vehicle',
              render: (_, r) => `${r.fleet_number} (${r.model})`,
            },
            {
              title: t('fleetOpsRec.columns.passengers'),
              key: 'passengers',
              width: 88,
              render: () =>
                passengerCount != null && Number.isFinite(passengerCount)
                  ? passengerCount
                  : '—',
            },
            {
              title: t('fleetOpsRec.columns.capacity'),
              dataIndex: 'capacity',
              key: 'capacity',
              width: 88,
            },
            {
              title: t('fleetOpsRec.columns.driver'),
              dataIndex: 'driver_name',
              key: 'driver_name',
            },
            {
              title: t('fleetOpsRec.columns.distance'),
              key: 'distance',
              render: (_, r) =>
                r.distance_meters != null ? formatDistance(r.distance_meters) : '—',
            },
            {
              title: t('fleetOpsRec.columns.score'),
              dataIndex: 'score',
              key: 'score',
              render: (v: number) => Math.round(v),
            },
            {
              title: t('fleetOpsRec.columns.action'),
              key: 'action',
              render: (_, r) => (
                <Button type="link" size="small" onClick={() => onSelect(r)}>
                  {t('fleetOpsRec.select')}
                </Button>
              ),
            },
          ]}
        />
      )}

      {loaded && rows.length > 0 && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          {t('fleetOpsRec.top', { recommendation: formatFleetOperatorRecommendation(rows[0]) })}
        </Text>
      )}
    </div>
  );
}

export default FleetOperatorRecommendationList;
